import {
  AnchorHTMLAttributes,
  ComponentType,
  createContext,
  DetailedHTMLProps,
  ReactElement,
  ReactNode,
  useContext,
  useMemo,
} from 'react';
import { LinkEditorType } from './ContextLinkEditor';
import { GenericLinkType } from './GenericLinkType';
import { LinkButtonComponent } from './LinkDecorator';

type AnchorProps = DetailedHTMLProps<AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>;

type LinkDecoratorProps = AnchorProps & { Component: LinkButtonComponent; href: string };

export type LinkDecoratorType = (props: LinkDecoratorProps) => ReactElement;

export interface LinkTypeExtension {
  /**
   * Unique Identifier for the type. Uniqueness is not enforced by the provider
   */
  id: string;
  /**
   * A name for the link type that can be displayed to the user
   */
  name: string;
  /**
   * A component for creating/editing a context link
   */
  Editor?: LinkEditorType;
  /**
   * A matching function for determining if a given url is of this link type
   *
   * @param url
   * @returns boolean
   */
  matcher: (url: string) => boolean;
  /**
   * A component for decorating a displayed link that matcher matches
   */
  Decorator?: LinkDecoratorType;
  /**
   * An icon that can be displayed alongside a link
   */
  Icon?: ComponentType;
}

type LinkTypeExtensionWithEditor = LinkTypeExtension & { Editor: LinkEditorType };

const ContextLinkTypesContext = createContext<LinkTypeExtension[]>([]);

export const ContextLinkTypesProvider = ({ value, children }: { children: ReactNode; value: LinkTypeExtension[] }) => {
  return <ContextLinkTypesContext.Provider value={value}>{children}</ContextLinkTypesContext.Provider>;
};

export const useContextLinkTypes = () => {
  const types = useContext(ContextLinkTypesContext);

  const typesWithFallback = useMemo(() => {
    return [...types, GenericLinkType];
  }, [types]);

  return typesWithFallback;
};

export const useLinkTypeByUrl = (url?: string) => {
  const linkTypes = useContextLinkTypes();

  // If no url, give the first editor type
  if (url === undefined) {
    return linkTypes[0] || GenericLinkType;
  }

  const linkType = linkTypes.find((link) => link.matcher(url));

  return linkType || GenericLinkType;
};

export const useLinkTypeById = (id?: string) => {
  const linkTypes = useContextLinkTypes();

  const linkType = linkTypes.find((link) => link.id === id);

  return linkType || GenericLinkType;
};

export const useLinkTypeWithEditor = (id?: string) => {
  const linkType = useLinkTypeById(id);

  if (linkType.Editor !== undefined) {
    return linkType as LinkTypeExtensionWithEditor;
  }

  return GenericLinkType as LinkTypeExtensionWithEditor;
};
